import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	adoptSession,
	liveSession,
	holdOutstanding,
	switchSession,
} from '../../agentBridge/core/sessionLifecycle';
import {
	findTopHfclNavigationHints,
	isAmbiguous,
	findHintById,
	isNavigationQuestion,
	isAffirmative,
	isNegative,
	HFCL_NAVIGATION_HINTS,
} from './hfclChatHints';
import {
	findSemanticHint,
	warmupSemantic,
} from './hfclSemanticFallback';
import { HFCL_USE_AGENT, HFCL_USE_OLLAMA } from './hfclChatConfig';
import { classifyWithBackend, emitChatEvent } from './hfclBackendClient';
import {sendAgentMessage} from '../../agentBridge';
import { getHfclChatContext } from './hfclChatContext';
import { installScrollToWidget } from './hfclScrollToWidget';

// Make sure the scroll-to-widget helper is exposed on window so the
// pseudo-action runner below can call it. Module-level invocation runs
// once at app boot; safe to call repeatedly.
installScrollToWidget();

/**
 * Quick local detector for "edit this thing" type messages. Works
 * without the LLM round-trip so we can immediately trigger the
 * registered editActionId for the current section.
 *
 * @param {string} text user message
 * @return {boolean} true if the message looks like a context-aware edit request
 */
const looksLikeEditCurrent = (text) => {
	const t = (text || '').toLowerCase().trim();
	return /^(edit|update|modify|change)(\s+(it|this|current|here|now))?[?.!]*$/.test(t) ||
		/^(edit|update|modify)\s+(this\s+)?(section|page|setting|settings|panel|tab|form)\b/.test(t);
};

// NOTE: `looksLikeSiteAction`, `looksLikeHelp`, and `HELP_CATEGORIES` used to
// live here. They've been moved to chat-backend/server.js (the `tryFastPath`
// function + HELP_PATTERNS / HELP_SUGGESTIONS / SITE_ACTION_PATTERNS
// constants). Reasons for the move:
//   1. Single source of truth — one place to update regex, one place to
//      audit chat traffic. Frontend never needs to re-deploy for a tweak.
//   2. Every user message hits the API now (consistent observability,
//      cleaner traceability, easier to add server-side analytics).
//   3. The frontend no longer risks the "open access points" → Yes bug
//      that happened when a local short-circuit ran before the matcher.

/**
 * Default welcome message shown when the chat panel is opened.
 */
export const HFCL_WELCOME_MESSAGE = 'How can I help you today?';
/**
 * Suggestion chips come from two different sources with two different
 * shapes:
 *   - `suggestions_for()` (plain intent match, no flow) — plain strings.
 *   - flow nodes (`flow_executor.py`'s `_render_node`) — objects
 *     `{ label, next_intent, restart }`.
 * Normalize both into the object shape once, at the point suggestions enter
 * chat state, so every downstream consumer (the panel's chip renderer, the
 * click handler) only ever deals with one shape.
 *
 * @param {Array<string|Object>} list
 * @return {Array<{label: string, next_intent: (string|null), restart: boolean}>}
 */
const normalizeSuggestions = (list) => {
	if (!Array.isArray(list)) return null;
	const out = list
		.map((item) => {
			if (typeof item === 'string') {
				return { label: item, next_intent: null, restart: false };
			}
			if (item && typeof item === 'object' && item.label) {
				return {
					label: String(item.label),
					next_intent: item.next_intent || null,
					choice_id: item.choice_id || null,
					restart: Boolean(item.restart),
					domain: item.domain || null,
					action: item.action || null,
				};
			}
			return null;
		})
		.filter(Boolean);
	return out.length ? out : null;
};

/**
 * localStorage key for the rolling history of user queries. We display the
 * most-recent few as one-tap suggestion chips below the welcome message
 * so returning users can re-run their last queries with a single click.
 */
const RECENT_QUERIES_KEY = 'hfcl-chat-recent-queries';
const RECENT_QUERIES_MAX = 3;

/**
 * Legacy localStorage key for the chat session id. Agent workflow state must
 * not outlive the visible in-memory conversation, so new code removes this
 * value instead of restoring it after a page reload or chatbot remount.
 */
const SESSION_ID_KEY = 'hfcl-chat-session-id';

const createSessionId = () =>
	`chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * How many recent turns to include in the backend payload. The LLM uses
 * them to resolve pronouns ("now configure it") and follow-ups. Larger
 * windows give better continuity but cost tokens; 6 is a sensible
 * production default (3 user + 3 assistant pairs).
 */
const HISTORY_TURN_LIMIT = 6;

/**
 * Create a session id for this mounted chat conversation.
 * Returns a string of the form `chat-<timestamp>-<random>`.
 *
 * @return {string}
 */
const getOrCreateSessionId = () => {
	try {
		if (typeof window === 'undefined') return 'chat-no-window';
		// Remove IDs written by older builds so a remount cannot attach the
		// welcome-only UI to an unrelated Redis workflow.
		window.localStorage.removeItem(SESSION_ID_KEY);
		return createSessionId();
	} catch (_) {
		// Storage cleanup is best effort; the session itself remains in memory.
		return createSessionId();
	}
};

/**
 * Wipe the persisted chat session id. Call this on logout if you want
 * the backend to treat the next interactions as a brand-new session.
 *
 * @return {void}
 */
export const resetChatSession = () => {
	try {
		if (typeof window !== 'undefined') {
			window.localStorage.removeItem(SESSION_ID_KEY);
		}
	} catch (_) { /* swallow */ }
};

/**
 * Read the recent-queries list from localStorage. Returns an empty array
 * if storage is unavailable or the entry is corrupt.
 *
 * @return {string[]}
 */
const readRecentQueries = () => {
	try {
		if (typeof window === 'undefined') return [];
		const raw = window.localStorage.getItem(RECENT_QUERIES_KEY);
		if (!raw) return [];
		const arr = JSON.parse(raw);
		if (!Array.isArray(arr)) return [];
		return arr.filter((q) => typeof q === 'string' && q.trim()).slice(0, RECENT_QUERIES_MAX);
	} catch (_) {
		return [];
	}
};

/**
 * Push a new query to the front of the recent-queries list and persist it.
 * Deduplicates case-insensitively and caps at RECENT_QUERIES_MAX entries.
 *
 * @param {string} query
 * @return {void}
 */
const recordRecentQuery = (query) => {
	try {
		if (typeof window === 'undefined') return;
		const clean = String(query || '').trim();
		if (!clean) return;
		// Skip very short or yes/no replies — they're not useful as recents.
		if (clean.length <= 2) return;
		if (/^(yes|no|y|n|ok|okay)\b/i.test(clean)) return;
		const existing = readRecentQueries();
		const norm = clean.toLowerCase();
		const next = [clean, ...existing.filter((q) => q.toLowerCase() !== norm)].slice(0, RECENT_QUERIES_MAX);
		window.localStorage.setItem(RECENT_QUERIES_KEY, JSON.stringify(next));
	} catch (_) {
		/* swallow — chat must keep working even if storage is full */
	}
};

/**
 * Delay between navigation and the post-nav DOM click. Long enough for the
 * destination route to render and mount its buttons.
 */
const POST_NAV_CLICK_DELAY_MS = 700;

/**
 * Find an element on the page by `data-chat-action="<id>"` and click it.
 * If the element is not yet in the DOM, retry a couple of times.
 *
 * Accepts either a single action id (string) or an array of ids that will be
 * clicked sequentially, with a short delay between each click. This is how
 * we chain a tab switch + a follow-up button click (e.g. "open Reports tab,
 * then click + Custom Reports").
 *
 * @param {string|string[]} actionOrChain
 * @return {void}
 */
const STEP_DELAY_MS = 400;

// How long to wait for the browser to finish the agent's commands before
// giving up and showing the progress note as an ordinary reply. Generous:
// resolving an entity calls Canvas over the network, and the DOM work that
// follows it (navigate, open a form, expand a section) is local but not
// instant.
const AGENT_WORK_TIMEOUT_MS = 20000;

/**
 * The tappable choices carried by an agent reply, from whichever field holds
 * them. A reply that offers choices is asking the user something, so it is
 * always an answer to render - never a progress note to hold back.
 *
 * @param {object} response agent reply
 * @return {Array<{label: string, choice_id: string}>} choices, possibly empty
 */
const readAgentSuggestions = (response) => {
	const options = Array.isArray(response?.choice_set?.options)
		? response.choice_set.options
		: Array.isArray(response?.suggestions)
			? response.suggestions
			: [];
	return options.map((item) => ({label: item.label, choice_id: item.id}));
};

/**
 * Poll-and-call a window-level helper that may not yet be installed (e.g.
 * `window.hfclOpenSite` is only registered while `GroupsDataTable` is
 * mounted — which happens AFTER the preceding `expand-widget:All Sites`
 * step in the chain). Times out after `maxMs` and silently no-ops.
 *
 * The helper is invoked WITHOUT awaiting — pseudo-actions are
 * fire-and-forget by design, so the chain doesn't block on the API call.
 *
 * @param {string} fnName window-property name (e.g. "hfclOpenSite")
 * @param {string} arg single string argument to pass to the helper
 * @param {number} [maxMs] how long to wait before giving up (default 2500)
 * @return {void}
 */
const invokeWhenReady = (fnName, arg, maxMs = 2500) => {
	if (typeof window === 'undefined') return;
	if (typeof window[fnName] === 'function') {
		try { window[fnName](arg); } catch (_) { /* swallow */ }
		return;
	}
	const start = Date.now();
	const timer = setInterval(() => {
		if (typeof window[fnName] === 'function') {
			clearInterval(timer);
			try { window[fnName](arg); } catch (_) { /* swallow */ }
			return;
		}
		if (Date.now() - start > maxMs) {
			clearInterval(timer);
		}
	}, 100);
};

/**
 * Pseudo-actions are not real DOM clicks — they call a globally exposed
 * setter function. Two formats are accepted:
 *
 *   "set-global-section:ntp"  →  window.hfclSetGlobalSettingsSection("ntp")
 *   "start-tour"              →  window.hfclStartTour("")           // no-arg
 *
 * Pages register these setters in their own useEffect. Returning true
 * means the action was handled; false means fall through to a DOM click.
 *
 * @param {string} actionId the action identifier, e.g. "set-global-section:ntp" or "start-tour"
 * @return {boolean} true if the pseudo-action was handled, false otherwise
 */
const handlePseudoAction = (actionId) => {
	if (!actionId || typeof actionId !== 'string') return false;
	const colon = actionId.indexOf(':');
	// Two valid forms:
	//   "verb:arg"  →  colon > 0,  verb = before, arg = after
	//   "verb"      →  no colon,   arg = ""
	// We rejected the colon < 1 case before and that broke any pseudo
	// action that doesn't need an argument (e.g. "start-tour").
	const verb = colon > 0 ? actionId.slice(0, colon) : actionId;
	const arg  = colon > 0 ? actionId.slice(colon + 1) : '';

	if (typeof window === 'undefined') return false;

	const PSEUDO = {
		'set-global-section': () =>
			typeof window.hfclSetGlobalSettingsSection === 'function' &&
			(window.hfclSetGlobalSettingsSection(arg), true),
		// Scroll a dashboard widget (graph card, accordion, etc.) into view
		// by its visible title text and pulse-highlight it for ~2 s.
		'scroll-to-widget': () =>
			typeof window.hfclScrollToWidget === 'function' &&
			window.hfclScrollToWidget(arg),
		// Same as scroll-to-widget but ALSO clicks the expand icon inside
		// the card so the graph opens in fullscreen.
		'expand-widget': () =>
			typeof window.hfclExpandWidget === 'function' &&
			window.hfclExpandWidget(arg),
		// Site-by-name actions — handled by GroupsDataTable while the
		// All Sites accordion is mounted. We start the async work but
		// don't await it (the runner is fire-and-forget); the helper polls
		// `window.hfclOpenSite` etc. in case the table hasn't finished
		// mounting yet from the preceding expand-widget step.
		'open-site': () => {
			invokeWhenReady('hfclOpenSite', arg);
			return true;
		},
		'configure-site': () => {
			invokeWhenReady('hfclConfigureSite', arg);
			return true;
		},
		'delete-site': () => {
			invokeWhenReady('hfclDeleteSite', arg);
			return true;
		},
		// New site toolbar actions — navigate to the site dashboard, then
		// fire the matching toolbar action via a CustomEvent that
		// GroupDashboardHeader listens for. groups.jsx does the lookup +
		// navigation; GroupDashboardHeader does the action.
		'clone-site': () => {
			invokeWhenReady('hfclCloneSite', arg);
			return true;
		},
		'import-site': () => {
			invokeWhenReady('hfclImportSiteConfig', arg);
			return true;
		},
		'export-site': () => {
			invokeWhenReady('hfclExportSiteConfig', arg);
			return true;
		},
		'refresh-site': () => {
			invokeWhenReady('hfclRefreshSite', arg);
			return true;
		},
		'download-site-pdf': () => {
			invokeWhenReady('hfclDownloadSitePdf', arg);
			return true;
		},
		// Client-by-name actions — handled by ClientDataTable while the
		// All Clients accordion is mounted. Same fire-and-forget pattern
		// as the site helpers.
		'open-client': () => {
			invokeWhenReady('hfclOpenClient', arg);
			return true;
		},
		'disconnect-client': () => {
			invokeWhenReady('hfclDisconnectClient', arg);
			return true;
		},
		// User-management actions — handled by Roles + Users tables on
		// /system?tab=user_management. "open" filters; "edit" filters and
		// opens the edit modal; "delete" / "suspend" filter and open the
		// confirm flow.
		'open-role': () => {
			invokeWhenReady('hfclOpenRole', arg);
			return true;
		},
		'edit-role': () => {
			invokeWhenReady('hfclEditRole', arg);
			return true;
		},
		'delete-role': () => {
			invokeWhenReady('hfclDeleteRole', arg);
			return true;
		},
		'open-user': () => {
			invokeWhenReady('hfclOpenUser', arg);
			return true;
		},
		'edit-user': () => {
			invokeWhenReady('hfclEditUser', arg);
			return true;
		},
		'delete-user': () => {
			invokeWhenReady('hfclDeleteUser', arg);
			return true;
		},
		'suspend-user': () => {
			invokeWhenReady('hfclSuspendUser', arg);
			return true;
		},
		// Product tour — invoked from "take a tour" intent. The handler
		// in navbar.jsx navigates to /dashboard, scrolls to top, and
		// dispatches the tour-modal redux action.
		'start-tour': () => {
			invokeWhenReady('hfclStartTour', '');
			return true;
		},
	};

	const handler = PSEUDO[verb];
	if (!handler) return false;
	try {
		return Boolean(handler());
	} catch (_) {
		return false;
	}
};

/**
 * Dispatch a full mouse-event sequence (mousedown → mouseup → click) on a
 * DOM element. Some MUI components (Box-as-tab, AccordionSummary, etc.)
 * wire their handlers on the pointer chain, not the basic click event, so
 * a bare `el.click()` is silently ignored. Dispatching the trio is what
 * makes the tab actually switch / accordion expand.
 *
 * @param {Element} el
 * @return {void}
 */
const robustClickEl = (el) => {
	if (!el) return;
	try {
		const opts = { bubbles: true, cancelable: true, view: window };
		el.dispatchEvent(new MouseEvent('mousedown', opts));
		el.dispatchEvent(new MouseEvent('mouseup', opts));
		if (typeof el.click === 'function') el.click();
		else el.dispatchEvent(new MouseEvent('click', opts));
	} catch (_) {
		try { el.click(); } catch (__) { /* swallow */ }
	}
};

/**
 * Click a `data-chat-action` element when it appears in the DOM, retrying
 * for up to ~2 seconds. Falls through to the `then` callback (if provided)
 * once the click runs, or once we give up.
 *
 * @param {string} actionId the action identifier (pseudo or DOM)
 * @param {Function} [then] callback to invoke after the click step
 * @return {void}
 */
const clickWhenReady = (actionId, then) => {
	// Try the pseudo-action path first — these don't need DOM polling.
	if (handlePseudoAction(actionId)) {
		if (typeof then === 'function') setTimeout(then, STEP_DELAY_MS);
		return;
	}

	let attempts = 0;
	const tryClick = () => {
		attempts += 1;
		const el = document.querySelector(`[data-chat-action="${actionId}"]`);
		if (el) {
			robustClickEl(el);
			if (typeof then === 'function') setTimeout(then, STEP_DELAY_MS);
			return;
		}
		if (attempts < 8) {
			setTimeout(tryClick, 250);
		} else if (typeof then === 'function') {
			// Could not find this step's element — try the next anyway, so a
			// partial chain still does something useful.
			then();
		}
	};
	tryClick();
};

/**
 * Run a chain of post-navigation actions in order. Accepts either a
 * single action id (string) or an array; arrays click each entry
 * sequentially with a small delay between steps.
 *
 * @param {string|string[]} actionOrChain action(s) to run
 * @return {void}
 */
const triggerPostNavAction = (actionOrChain) => {
	if (!actionOrChain || typeof document === 'undefined') return;
	const chain = Array.isArray(actionOrChain) ? actionOrChain : [actionOrChain];
	if (chain.length === 0) return;

	let i = 0;
	const runNext = () => {
		if (i >= chain.length) return;
		const step = chain[i];
		i += 1;
		clickWhenReady(step, runNext);
	};
	setTimeout(runNext, POST_NAV_CLICK_DELAY_MS);
};

/**
 * Execute an actions[] array returned by the flow layer.
 *
 * The backend flow executor produces a small vocabulary of actions:
 *   { type: "navigate", path: "/Maintenance" }
 *   { type: "click",    target: "firmware-add" }
 *   { type: "emit",     event: "chat-notice", detail: {...} }   // future
 *
 * We run them sequentially with the same delays / robustness as the
 * classic postNavAction chain, so behaviour is identical whether the
 * chain came from an intent's postNavAction OR from a flow node.
 *
 * Unknown action types are logged and skipped — this keeps forward
 * compatibility: adding a new action.type on the backend can't crash
 * older frontends.
 *
 * @param {Array<Object>} actions
 * @param {Function} navigate react-router navigate() function
 * @return {void}
 */
const runFlowActions = (actions, navigate) => {
	if (!Array.isArray(actions) || actions.length === 0) return;

	let i = 0;
	const runNext = () => {
		if (i >= actions.length) return;
		const step = actions[i];
		i += 1;
		if (!step || typeof step !== 'object') {
			runNext();
			return;
		}
		switch (step.type) {
			case 'navigate':
				if (step.path && typeof navigate === 'function') {
					navigate(step.path);
				}
				setTimeout(runNext, POST_NAV_CLICK_DELAY_MS);
				break;
			case 'click':
				if (step.target) {
					clickWhenReady(step.target, runNext);
				} else {
					runNext();
				}
				break;
			case 'emit':
				if (step.event && typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent(step.event, { detail: step.detail || {} }));
				}
				setTimeout(runNext, STEP_DELAY_MS);
				break;
			default:
				console.warn('[hfcl-chat] unknown flow action type:', step.type);
				runNext();
				break;
		}
	};
	setTimeout(runNext, POST_NAV_CLICK_DELAY_MS);
};

const makeId = (prefix) =>
	`${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Build a navigation `offer` from a hint. The offer is what gets stored on
 * a chat message so a Yes-button click can route there later.
 *
 * Hints flagged with `stayOnCurrentPath: true` (e.g. the navbar alarm
 * summary) need their path resolved at click-time, not at offer-build
 * time — because the user may be on any page when they invoke them. We
 * carry the flag forward; performNavigation reads `window.location` then.
 *
 * @param {Object} hint
 * @return {{ path: string, label: string, postNavAction: any, stayOnCurrentPath: boolean }}
 */
const buildOffer = (hint) => ({
	path: hint.path,
	label: hint.navigateLabel || hint.label,
	postNavAction: hint.postNavAction || null,
	stayOnCurrentPath: Boolean(hint.stayOnCurrentPath),
});

/**
 * Build the assistant reply payload for a single matched hint.
 *
 * @param {Object} hint
 * @return {{prompt: string, offer: Object}}
 */
/**
 * Turn a raw hint description into a warm, first-person sentence a new user
 * can read naturally. We do two things:
 *
 *   1. Swap UI jargon for plain-English words the user actually sees on
 *      screen — "accordion" is a UI term that means nothing to a new
 *      operator; "section" is what they'd call the same thing.
 *
 *   2. Rewrite imperative openings ("Open the ...", "Show the ...") as
 *      first-person promises ("I'll open the ...", "I'll show the ...").
 *      This turns instructions-at-the-user into things-the-bot-will-do
 *      FOR the user — much friendlier for a first-time reader.
 *
 * Only applies when the sentence clearly starts with a known imperative
 * verb followed by an article/pronoun. If the description doesn't match
 * (e.g. "Access Point Settings section under Global Settings.") we leave
 * it alone so we never mangle a noun phrase into ungrammatical text.
 */
const IMPERATIVE_VERBS_RX = /^(Open|Show|View|Go|Configure|Edit|Add|Create|Delete|Update|Set|Enable|Disable|Connect|Disconnect|Download|Upload|Import|Export|Refresh|Reload|Reset|Clone|Duplicate|Copy|Move|Cancel|Save|Submit|Approve|Reject|Search|Filter|Sort|Select|Start|Switch|Take|Bring|Change)(\s+(?:the|a|an|your|my|all|these|those|this|that))\b/;

const humanizeDescription = (raw) => {
	if (!raw) return '';
	let out = String(raw).trim();
	// (1) plain-English swaps
	out = out.replace(/\baccordions?\b/gi, 'section');
	out = out.replace(/\bmodal\b/gi, 'form');
	out = out.replace(/\bdialog\b/gi, 'form');
	// (2) imperative → first-person promise
	const m = out.match(IMPERATIVE_VERBS_RX);
	if (m) {
		out = `I'll ${m[1].toLowerCase()}${m[2]}${out.slice(m[0].length)}`;
	}
	return out;
};

const buildNavReply = (hint) => {
	const offer = buildOffer(hint);
	// Reply is written for a NEW user who has never seen this app. Three
	// pieces, in order:
	//   1. Warm acknowledgement — "I can help with that!".
	//   2. A first-person promise saying what will happen ("I'll open ...").
	//   3. A short, obvious next step referring to the Yes button visible
	//      right below the message.
	//
	// We deliberately don't repeat the navigate path ("Home Dashboard >
	// Global SSIDs (Add SSID)") because it reads as jargon. The user sees
	// what happens the moment they tap Yes; the reply just needs to feel
	// like a friendly colleague, not a system prompt.
	const promise = humanizeDescription(hint.description);
	const prompt = [
		"Sure — I can help with that!",
		"",
		promise,
		"",
		"Tap Yes below and I'll open it for you.",
	].join('\n');
	return { prompt, offer };
};

// Shortcut chips shown when a conversation naturally closes — user said
// "no" to an offer, typed a bare "no" out of context, or hit the final
// fallback. Gives them an obvious next step instead of a dead end.
const CLOSE_OUT_SUGGESTIONS = [
	'help',
	'open dashboard',
	'show me critical alarms',
	'open user management',
	'take a tour',
];

/**
 * Build a disambiguation reply for multiple candidate hints. Returns a
 * short prompt and an array of `offers` that the panel renders as
 * clickable chips.
 *
 * @param {Object[]} hints
 * @return {{ prompt: string, offers: Object[] }}
 */
const buildDisambiguationReply = (hints) => {
	const offers = hints.map(buildOffer);
	const lines = [
		"Happy to help — which one did you mean?",
		'',
		...offers.map((o, i) => `${i + 1}. ${o.label}`),
		'',
		'Just tap the one you want and I\'ll open it.',
	];
	return { prompt: lines.join('\n'), offers };
};

/**
 * HFCL Chat Bot state hook.
 *
 * Routing strategy (cheapest first):
 *   1. Yes / No reply to a pending offer.
 *   2. Static token + Levenshtein keyword match (synchronous, instant).
 *   3. Semantic embedding match via @xenova/transformers (async, lazy).
 *   4. Friendly fallback message.
 *
 * @return {object} { messages, loading, sendMessage, navigateFromOffer }
 */
// Build the welcome bubble that sits at the top of every fresh chat. Also
// reused by resetChat() when the user closes the chat with the X button.
//
// A fresh chat intentionally does NOT surface recent-query chips or any
// "pick up where you left off" text — every fresh chat is a clean slate
// so a new user isn't confronted with someone else's history and the
// returning user has an unambiguous entry point. Starter topic chips are
// rendered separately in the panel footer (Zeplin suggestion row).
const buildWelcomeMessage = () => ({
	id: 'welcome-hfcl-chatbot',
	role: 'assistant',
	content: HFCL_WELCOME_MESSAGE,
	createdAt: Date.now(),
});

export const useHfclChat = () => {
	const [messages, setMessages] = useState(() => [buildWelcomeMessage()]);
	const [pendingNavigation, setPendingNavigation] = useState(null);
	const [loading, setLoading] = useState(false);
	// What the agent is doing right now, shown beside the typing dots rather
	// than as its own bubble. The agent answers a request in two turns: it
	// replies with commands for the browser to run ("Looking up the matching
	// site."), then replies again once they are done ("The form is open.").
	// Only the second one is an answer; the first describes internal work and
	// stacking it as a bubble left the user reading our own progress notes.
	const [statusLine, setStatusLine] = useState(null);
	const statusTextRef = useRef(null);
	const statusTimerRef = useRef(null);

	// Stable session id used as a correlation key with the backend. A ref
	// (not state) — it never triggers a re-render and the value is the
	// same for the entire lifetime of the hook.
	const sessionIdRef = useRef(null);
	// Every chat request still in flight, so leaving a conversation can stop
	// them rather than only ignore their answers. A Set because a user can
	// send again before the first has settled, and each turn removes its own.
	const abandonInFlightRef = useRef(new Set());
	/** Stop every request this conversation still has running. */
	const abandonInFlight = useCallback(() => {
		for (const abandon of Array.from(abandonInFlightRef.current)) {
			try {
				abandon();
			} catch (_) {
				// An already-settled turn is not worth a thrown error.
			}
		}
		abandonInFlightRef.current.clear();
	}, []);
	if (sessionIdRef.current === null) {
		sessionIdRef.current = getOrCreateSessionId();
		// Adopting rather than switching: nothing is stale, there is simply
		// now an answer to "which conversation is this".
		adoptSession(sessionIdRef.current);
	}

	// Kick off background warm-up of the semantic model the first time the
	// hook mounts. The download is ~25 MB; the browser will cache it after.
	useEffect(() => {
		warmupSemantic(HFCL_NAVIGATION_HINTS);
	}, []);

	// react-router's navigate — used by runFlowActions when we execute
	// backend-driven flow actions outside of sendMessage (e.g. the
	// window.hfclEmitChatEvent helper, defined further down after
	// pushAssistant is in scope).
	const emitNavigate = useNavigate();

	// `extras` can carry one of:
	//   - `{ path, label, postNavAction }` (single Yes/No offer — backward compat)
	//   - `{ navigation }` (same as above, explicit)
	//   - `{ navigations: [...] }` (disambiguation — render each as a chip)
	//   - `{ suggestions: ['...', '...'] }` (text phrases — clicking sends the phrase)
	const pushAssistant = useCallback((content, extras = null) => {
		const looksLikeOffer = extras && !Array.isArray(extras) &&
			!extras.navigations && !extras.suggestions && !extras.navigation &&
			!extras.unsaved;
		const navigation = looksLikeOffer ? extras : extras?.navigation || null;
		const navigations = extras?.navigations || null;
		const suggestions = normalizeSuggestions(extras?.suggestions);
		setMessages((prev) => [
			...prev,
			{
				id: makeId('a'),
				role: 'assistant',
				content,
				navigation,
				navigations,
				suggestions,
				// The backend could not write this turn to the transcript.
				// The answer is real and was delivered; it just will not be
				// there when the conversation is reopened. Saying so beats a
				// user believing they have a record of it.
				unsaved: Boolean(extras?.unsaved),
				createdAt: Date.now(),
			},
		]);
	}, []);

	// Drop the status line without saying it - the turn reached an answer, or
	// it was abandoned, and either way the note about work in progress is
	// no longer true.
	const clearStatus = useCallback(() => {
		if (statusTimerRef.current) {
			clearTimeout(statusTimerRef.current);
			statusTimerRef.current = null;
		}
		statusTextRef.current = null;
		setStatusLine(null);
	}, []);

	// Hold the agent's progress note next to the spinner while the browser
	// carries out its commands.
	const holdStatus = useCallback((text) => {
		if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
		statusTextRef.current = text || null;
		setStatusLine(text || null);
		// A command can throw, and a callback can go missing - we hit exactly
		// that with a rejected /api/agent/events payload. Held quietly, that
		// leaves the panel on a spinner with nothing to read. Give up after a
		// while and say the last thing we knew, so the turn still ends.
		statusTimerRef.current = setTimeout(() => {
			statusTimerRef.current = null;
			const held = statusTextRef.current;
			statusTextRef.current = null;
			setStatusLine(null);
			setLoading(false);
			if (held) pushAssistant(held);
		}, AGENT_WORK_TIMEOUT_MS);
	}, [pushAssistant]);

	useEffect(() => () => {
		if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
		// Unmounting ends every turn this panel was waiting on. The answers
		// have nowhere to go, and the connections should not outlive the
		// thing that asked for them.
		abandonInFlight();
	}, [abandonInFlight]);

	const pushUser = useCallback((content) => {
		setMessages((prev) => [
			...prev,
			{ id: makeId('u'), role: 'user', content, createdAt: Date.now() },
		]);
		// Persist this query so we can offer it as a one-tap chip next time
		// the chat opens. recordRecentQuery handles its own validation
		// (skips Yes/No replies, very short input, etc.).
		recordRecentQuery(content);
	}, []);

	// ------------------------------------------------------------------
	// Flow-event bridge for the outside world.
	//
	// Register `window.hfclEmitChatEvent(name, data?)` so any component
	// in the app (forms, modals, table refreshers, ...) can signal step
	// completion into the currently-active flow WITHOUT importing this
	// hook. Example:
	//
	//   // AddFirmareImage.jsx — after a successful save
	//   window.hfclEmitChatEvent?.('firmware-uploaded', {
	//     firmware_name: form.version,
	//   });
	//
	// The backend advances the flow's state machine and returns the next
	// node's reply / actions / suggestions. We then push that into the
	// chat as if the user had said something to trigger it.
	//
	// IMPORTANT: this effect must be declared AFTER `pushAssistant` so
	// the closure and dep array can reference it without hitting the TDZ.
	// If no flow is currently waiting on this event the backend returns
	// 204 and we no-op — safe to call from anywhere without checking.
	// ------------------------------------------------------------------
	useEffect(() => {
		if (typeof window === 'undefined') return undefined;
		window.hfclEmitChatEvent = async (event, data = {}) => {
			try {
				const flowResp = await emitChatEvent({
					sessionId: sessionIdRef.current,
					event,
					data,
				});
				if (!flowResp) return null;
				// Behave the same way as a classify response with actions.
				if (Array.isArray(flowResp.actions) && flowResp.actions.length > 0) {
					runFlowActions(flowResp.actions, emitNavigate);
				}
				const chips = Array.isArray(flowResp.suggestions) && flowResp.suggestions.length
					? { suggestions: flowResp.suggestions }
					: null;
				if (flowResp.reply) {
					pushAssistant(flowResp.reply, chips);
				}
				return flowResp;
			} catch (err) {
				console.warn('[hfcl-chat] hfclEmitChatEvent failed:', err && err.message);
				return null;
			}
		};
		return () => {
			try { delete window.hfclEmitChatEvent; } catch (_) { /* ignore */ }
		};
	}, [emitNavigate, pushAssistant]);

	// Listen for async notices from other parts of the app. Today this is
	// `site-not-found` and `client-not-found` coming out of the data
	// tables after a deep pagination scan misses, but the channel is
	// generic — anything that calls
	//   window.dispatchEvent(new CustomEvent('hfcl-chat-notice', { detail: {kind, ...} }))
	// will land here and can push a chat bubble without importing this
	// hook directly.
	useEffect(() => {
		if (typeof window === 'undefined') return undefined;
		const handler = (ev) => {
			const detail = ev?.detail || {};
			if (detail.kind === 'site-not-found' && detail.name) {
				const verb = detail.action === 'configure' ? 'configure'
					: detail.action === 'delete' ? 'delete'
					: 'open';
				pushAssistant(
					`I couldn't find a site named "${detail.name}" to ${verb}. ` +
					`Check the spelling, try a partial name (e.g. just "${String(detail.name).split(/\s+/)[0]}"), ` +
					`or use the + Add Site button on the dashboard.`
				);
			} else if (detail.kind === 'client-not-found' && detail.name) {
				const verb = detail.action === 'disconnect' ? 'disconnect' : 'open';
				pushAssistant(
					`I couldn't find a client matching "${detail.name}" to ${verb}. ` +
					`You can search by username (e.g. "Jio_User77") or MAC address ` +
					`("00:03:7f:11:11:48"). Check spelling, or open the All Clients ` +
					`table on the dashboard to browse.`
				);
			} else if (detail.kind === 'role-not-found' && detail.name) {
				const verb = detail.action === 'edit' ? 'edit'
					: detail.action === 'delete' ? 'delete'
					: 'find';
				pushAssistant(
					`I couldn't ${verb} role "${detail.name}" — no role by that name. ` +
					`The roles table on Canvas Settings > User Management is already ` +
					`filtered to your query, so you can check the spelling there.`
				);
			} else if (detail.kind === 'user-not-found' && detail.name) {
				const verb = detail.action === 'edit' ? 'edit'
					: detail.action === 'delete' ? 'delete'
					: detail.action === 'suspend' ? 'suspend'
					: 'find';
				pushAssistant(
					`I couldn't ${verb} user "${detail.name}" — no user matched by ` +
					`username, full name, or email. The Users table on Canvas Settings ` +
					`> User Management is filtered to your query — check the spelling.`
				);
			}
		};
		window.addEventListener('hfcl-chat-notice', handler);
		return () => window.removeEventListener('hfcl-chat-notice', handler);
	}, [pushAssistant]);

	useEffect(() => {
		if (typeof window === 'undefined') return undefined;
		const handler = (event) => {
			if (
				event.detail?.sessionId &&
				event.detail.sessionId !== sessionIdRef.current
			) return;
			const response = event.detail?.response || event.detail;
			if (!response?.message) return;
			// The bridge drains the whole command loop before it publishes,
			// so this is the end of the turn: retire the status line and let
			// the answer stand on its own.
			clearStatus();
			setLoading(false);
			const suggestions = readAgentSuggestions(response);
			pushAssistant(
				response.message,
				suggestions.length ? {suggestions} : null,
			);
		};
		window.addEventListener('canvas-agent:reply', handler);
		return () => window.removeEventListener('canvas-agent:reply', handler);
	}, [pushAssistant, clearStatus]);

	const performNavigation = useCallback((offer, navigate) => {
		if (!offer || typeof navigate !== 'function') return;
		// Offers flagged `stayOnCurrentPath` (e.g. the navbar alarm popover)
		// shouldn't bounce the user off the page they're on. We resolve the
		// path to the current pathname so react-router's same-route
		// navigate() is a no-op and only the postNavAction click fires.
		const targetPath = offer.stayOnCurrentPath && typeof window !== 'undefined'
			? window.location.pathname + window.location.search
			: offer.path;
		if (!targetPath) return;
		navigate(targetPath);
		if (offer.postNavAction) {
			triggerPostNavAction(offer.postNavAction);
		}
	}, []);

	const navigateFromOffer = useCallback(
		(offer, navigate) => {
			performNavigation(offer, navigate);
			setPendingNavigation(null);
			pushAssistant(`Great — taking you there now.`);
		},
		[performNavigation, pushAssistant]
	);

	const sendMessage = useCallback(
		async (text, navigate, opts = {}) => {
			const clean = text?.trim();
			if (!clean) return;
			// A selection comes from a click and from nowhere else.
			//
			// This used to match typed text against the labels on screen and,
			// on a hit, send the label instead of the sentence plus the
			// option's id - which the backend reads as a clicked choice and
			// therefore does not interpret. So typing a word that happened to
			// appear in a visible label became a click on that option: the
			// user's own words never reached the model, and the trace of the
			// turn held a label nobody had pressed.
			//
			// Typed answers still work, and better: the backend resolves them
			// against its own list of what is actually visible to this user,
			// accepts "third" as well as "3", and runs interpretation first,
			// so a question asked while a menu is open is read as a question.
			// Two half-copies of that logic, one of them weaker and on the
			// wrong side of the wire, is what produced the bypass.
			const resolvedIntent = opts.intent || null;
			const resolvedChoiceId = opts.choiceId || null;
			// What the user typed, exactly. A click sends its own ids above.
			const outbound = clean;

			pushUser(clean);

			// The resource agent owns structured resource actions. It returns
			// either two server-authorized choices or normal allowlisted Canvas
			// commands; form validation remains entirely in Canvas.
			if (HFCL_USE_AGENT) {
				// The session this particular request belongs to. Captured now,
				// before the await, so a New Chat / restored-thread switch that
				// happens while this is in flight can be detected when it
				// resolves: sessionIdRef.current will have moved on, and this
				// turn's reply, status line and loading spinner must stay dead
				// rather than bleed into whatever conversation is on screen by
				// then. The message is still safely recorded either way - the
				// backend persists it server-side by session id regardless of
				// what the client goes on to do with the reply.
				const requestSessionId = sessionIdRef.current;
				const requestGeneration = liveSession().generation;
				// Whether this turn's reply still belongs on screen.
				//
				// The session id alone is not enough, and A -> B -> A is why:
				// leave a conversation, come back to it, and the id matches
				// again while the turn in flight belongs to the visit before
				// the one showing. The generation stamp is what separates
				// them, it was already being captured for the bridge event,
				// and every other guard here was checking only the id.
				//
				// One predicate for all of them - reply, status, error,
				// spinner - because a stale turn that is silenced in three
				// places out of four is not silenced.
				const stillOurs = () => (
					sessionIdRef.current === requestSessionId
					&& liveSession().generation === requestGeneration
				);
				// The turn's own cancellation, wired to this conversation's
				// life.
				//
				// The guards above stop a stale reply reaching the screen;
				// they do not stop the request. Without a signal the fetch
				// ran to completion after a reset, holding a connection and
				// the model behind it for an answer nobody would read.
				//
				// Aborting is not a claim the backend stopped - it has not -
				// which is why `stillOurs` stays exactly as it was.
				const inFlight = typeof AbortController === 'function'
					? new AbortController()
					: null;
				const abandon = () => inFlight?.abort();
				abandonInFlightRef.current.add(abandon);
				// Registered with the shared lifecycle as well as locally.
				//
				// The local set is reached by reset and unmount, which this
				// hook owns. Switching conversations does not go through
				// either - the panel calls `switchSession` directly, which
				// cancels the shared collection this was never in - so a
				// request left running when the user opened another thread
				// kept running.
				const releaseShared = inFlight
					? holdOutstanding(inFlight)
					: () => {};
				setLoading(true);
				clearStatus();
				// Whether this reply handed work to the browser. If it did,
				// the turn is not over and the spinner has to stay on until
				// the bridge reports back.
				let handedToBridge = false;
				try {
					const response = await sendAgentMessage(outbound, {
						sessionId: requestSessionId,
						selectedCapabilityId: resolvedIntent,
						selectedChoiceId: resolvedChoiceId,
						signal: inFlight?.signal,
					});
					if (!stillOurs()) {
						// Abandoned mid-flight: this reply belongs to a
						// conversation that is no longer the one showing, or
						// to an earlier visit to this one. Drop it silently -
						// no bubble, no status line, no command dispatch -
						// instead of letting it surface wherever the user has
						// since navigated.
						return;
					}
					const suggestions = readAgentSuggestions(response);
					handedToBridge = Boolean(response.commands?.length) &&
						!suggestions.length;
					if (handedToBridge) {
						holdStatus(response.message);
					} else if (response.message) {
						const unsaved = response.transcript_saved === false;
						pushAssistant(
							response.message,
							suggestions.length || unsaved
								? {suggestions, unsaved}
								: null,
						);
					}
					window.dispatchEvent(new CustomEvent('canvas-agent:response', {
						detail: {
							response,
							sessionId: requestSessionId,
							// Stamped when the request went out. The bridge
							// used to read the generation as the reply
							// arrived, which made a reply that crossed a
							// reset look current by construction.
							sessionGeneration: requestGeneration,
						},
					}));
					return;
				} catch (failure) {
					if (!stillOurs()) return;
					clearStatus();
					// A turn the user cancelled is not a failure and says
					// nothing. Leaving a conversation should not leave an
					// apology behind in it.
					if (!failure?.cancelled) {
						pushAssistant(
							failure?.timeout
								? 'That took longer than expected, so I stopped waiting. Please try again.'
								: 'The resource assistant is temporarily unavailable. Please try again.',
						);
					}
					return;
				} finally {
					// Released once the turn has settled, however it settled:
					// a controller left in the set aborts a request that has
					// already finished, which is harmless and misleading.
					abandonInFlightRef.current.delete(abandon);
					releaseShared();
					if (!handedToBridge && stillOurs()) {
						setLoading(false);
					}
				}
			}

			// Site-by-name actions and /help used to be handled locally with
			// `looksLikeSiteAction` and `looksLikeHelp`. They've been moved
			// to the Node backend's fast-path classifier so every chat
			// message hits the API (cleaner audit trail, single source of
			// truth, regex changes ship without a frontend rebuild). The
			// backend returns the same shape Ollama would; the
			// `open-site` / `open-configure-site` / `open-delete-site`
			// branch in the Ollama section below builds the offer from
			// `result.args`.
			//
			// 1a. "Edit" said on a section that has registered an editActionId
			//     in the current page context — click the Edit button directly.
			//     This fires before the static matcher / backend so context
			//     always wins over a generic "edit" misclassification.
			if (looksLikeEditCurrent(clean)) {
				const ctx = getHfclChatContext();
				if (ctx?.editActionId) {
					const offer = {
						path: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
						label: ctx.sectionLabel || ctx.section || 'this section',
						postNavAction: ctx.editActionId,
					};
					performNavigation(offer, navigate);
					pushAssistant(`Sure — opening ${offer.label} in edit mode.`);
					return;
				}
			}

			// 1. Yes / No reply to a previous offer.
			if (pendingNavigation) {
				if (isAffirmative(clean)) {
					performNavigation(pendingNavigation, navigate);
					pushAssistant(`Great — opening it now.`);
					setPendingNavigation(null);
					return;
				}
				if (isNegative(clean)) {
					// Close the loop with quick-restart chips so the user
					// always has an obvious next step. Don't just say
					// "No problem." and leave them stranded.
					pushAssistant('Okay — cancelled. Anything else I can help with?', {
						suggestions: CLOSE_OUT_SUGGESTIONS,
					});
					setPendingNavigation(null);
					return;
				}
				// Anything else clears the pending offer and falls through.
				setPendingNavigation(null);
			}

			// 1b. Bare "no" / "cancel" / "never mind" with NO pending offer.
			// Legacy chat can close this locally. Agent chat must send it to
			// the backend so the persisted workflow is actually cancelled;
			// otherwise the next prompt is incorrectly collected as the old
			// workflow's missing slot.
			if (isNegative(clean) && !HFCL_USE_AGENT) {
				pushAssistant('Okay — no problem. Anything else I can help with?', {
					suggestions: CLOSE_OUT_SUGGESTIONS,
				});
				return;
			}

			// Local helper — used by both the Ollama and static paths below to
			// emit either a single-offer reply (when one hint dominates) or a
			// disambiguation reply with chips (when several candidates are
			// close in score).
			//
			// `preferredHint` (optional) is included as the first chip when
			// the caller has a strong upstream choice (e.g. Ollama's pick).
			const emitNavReply = (preferredHint) => {
				const staticTop = findTopHfclNavigationHints(clean, { limit: 3 });
				const candidates = [];
				if (preferredHint) candidates.push(preferredHint);
				for (const { hint } of staticTop) {
					if (!candidates.some((h) => h.id === hint.id)) {
						candidates.push(hint);
					}
				}
				if (candidates.length === 0) return false;

				// HIGH-CONFIDENCE static override:
				// When the static matcher's top hit has a very strong score
				// (a long multi-token keyword exactly present in the
				// message, e.g. "bulk add sites" → score 143), we trust it
				// and skip disambiguation entirely — even if Ollama or some
				// other upstream returned a less specific intent. This
				// prevents queries like "bulk add sites" from showing 3
				// chips (the specific bulk-add-sites card AND the generic
				// bulk-operations tab AND dash-add-site) when the user's
				// intent is unambiguous.
				//
				// Threshold of 130 catches every 2-token keyword of length
				// >= 12 chars and every 3-token keyword of length >= 11.
				const HIGH_CONFIDENCE_SCORE = 130;
				const staticWinner = staticTop[0];
				if (staticWinner && staticWinner.score >= HIGH_CONFIDENCE_SCORE) {
					const { prompt, offer } = buildNavReply(staticWinner.hint);
					setPendingNavigation(offer);
					pushAssistant(prompt, offer);
					return true;
				}

				// Ambiguous if (a) static itself flagged ambiguity, or (b) we
				// have an upstream pick that disagrees with the static winner.
				const staticIsAmbiguous = isAmbiguous(staticTop);
				const upstreamDisagrees = Boolean(
					preferredHint &&
					staticTop.length > 0 &&
					staticTop[0].hint.id !== preferredHint.id
				);

				if ((staticIsAmbiguous || upstreamDisagrees) && candidates.length >= 2) {
					const top = candidates.slice(0, 3);
					const { prompt, offers } = buildDisambiguationReply(top);
					setPendingNavigation(null);
					pushAssistant(prompt, { navigations: offers });
					return true;
				}

				const { prompt, offer } = buildNavReply(candidates[0]);
				setPendingNavigation(offer);
				pushAssistant(prompt, offer);
				return true;
			};

			// 2. Ollama backend — only when the feature flag is on. If the
			// backend returns a valid intent we use it; otherwise fall
			// through to the local static matcher.
			if (HFCL_USE_OLLAMA) {
				setLoading(true);
				try {
					const liveContext = getHfclChatContext();
					const pageContext = {
						// Merge whatever the current page registered (tab,
						// section, editActionId, etc.) so the backend can
						// pick the right intent under conflict.
						...liveContext,
						route: typeof window !== 'undefined' ? window.location.pathname : null,
						input_mode: opts.fromVoice ? 'voice' : 'text',
						// Domain lock signal for BE domain-first classify.
						domain: liveContext.domain || null,
					};
					if (opts.fromVoice && opts.voiceLang) {
						pageContext.speech_lang = opts.voiceLang;
					}
					// Carry the last N turns and the stable session id so
					// the backend can build a context-aware Ollama prompt.
					// Pronouns and follow-ups ("now configure it",
					// "delete that one") resolve correctly with this.
					// `messages` already has the user message we just
					// pushed; drop the welcome bubble + slice the tail.
					const history = messages
						.filter((m) => m.role === 'user' || m.role === 'assistant')
						.filter((m) => m.id !== 'welcome-hfcl-chatbot')
						.slice(-HISTORY_TURN_LIMIT)
						.map((m) => ({ role: m.role, text: String(m.content || '') }));
					const result = await classifyWithBackend(clean, {
						pageContext,
						sessionId: sessionIdRef.current,
						history,
						// A suggestion chip with a resolved intent id sends it
						// here so the backend skips classification entirely
						// instead of re-guessing from the chip's label text.
						intent: resolvedIntent,
					});
					if (result) {
						// ----- Flow-driven response (highest priority) --------
						// If the backend returned a flow response (actions[] + optional
						// suggestions + flow_state), it's driving the conversation
						// through a multi-step workflow. Execute the actions, show
						// the reply, and let the flow's own suggestions become
						// chips — bypass the normal intent-routing tree.
						if (Array.isArray(result.actions) && result.actions.length > 0) {
							runFlowActions(result.actions, navigate);
							const chips = Array.isArray(result.suggestions) && result.suggestions.length
								? { suggestions: result.suggestions }
								: null;
							if (result.reply) {
								pushAssistant(result.reply, chips);
							} else if (chips) {
								// Actions with no reply — still show the chips so the
								// user has next steps.
								pushAssistant('Done.', chips);
							}
							return;
						}

						if (result.type === 'action' && result.intent) {
							// ----- Fast-path intents from the backend ------------
							// The Node backend's tryFastPath() routes site- and
							// client-by-name queries to these synthetic
							// intents (no Ollama hop). The args carry the
							// extracted name. We build the offer chain
							// dynamically; the chain (a) expands the relevant
							// accordion to mount the data table — which
							// registers the window.hfcl*Site / window.hfcl*Client
							// helpers — then (b) calls the right pseudo-action
							// with the name baked in.
							//
							// UX rule: open / configure are non-destructive AND
							// the user already gave a specific, unambiguous
							// instruction. Asking "do you want me to proceed?"
							// after they typed "open site Yukta_wifi6" is
							// pointless friction — we just execute. Destructive
							// variants (delete-site, disconnect-client) keep
							// the Yes/No confirmation.
							const fastPath = (() => {
								switch (result.intent) {
									case 'open-site':              return { kind: 'site',   action: 'open-site',         verb: 'Open',     label: 'site',   expand: 'All Sites',   destructive: false };
									case 'open-configure-site':    return { kind: 'site',   action: 'configure-site',    verb: 'Configure',label: 'site',   expand: 'All Sites',   destructive: false };
									case 'open-clone-site':        return { kind: 'site',   action: 'clone-site',        verb: 'Clone',    label: 'site',   expand: 'All Sites',   destructive: false };
									case 'open-import-site':       return { kind: 'site',   action: 'import-site',       verb: 'Import',   label: 'site',   expand: 'All Sites',   destructive: false };
									case 'open-export-site':       return { kind: 'site',   action: 'export-site',       verb: 'Export',   label: 'site',   expand: 'All Sites',   destructive: false };
									case 'open-refresh-site':      return { kind: 'site',   action: 'refresh-site',      verb: 'Refresh',  label: 'site',   expand: 'All Sites',   destructive: false };
									case 'open-download-site-pdf': return { kind: 'site',   action: 'download-site-pdf', verb: 'Download', label: 'site',   expand: 'All Sites',   destructive: false };
									case 'open-delete-site':       return { kind: 'site',   action: 'delete-site',       verb: 'Delete',   label: 'site',   expand: 'All Sites',   destructive: true  };
									case 'open-client':            return { kind: 'client', action: 'open-client',       verb: 'Open',     label: 'client', expand: 'All Clients', destructive: false };
									case 'open-disconnect-client': return { kind: 'client', action: 'disconnect-client', verb: 'Disconnect',label: 'client',expand: 'All Clients', destructive: true  };
									// ----- User Management -----
									// path = /system?tab=user_management (no accordion expansion;
									// the page mounts Roles + Users tables directly).
									case 'open-role-by-name':      return { kind: 'role',   action: 'open-role',         verb: 'Open',     label: 'role',   path: '/system?tab=user_management', destructive: false };
									case 'edit-role-by-name':      return { kind: 'role',   action: 'edit-role',         verb: 'Edit',     label: 'role',   path: '/system?tab=user_management', destructive: false };
									case 'delete-role-by-name':    return { kind: 'role',   action: 'delete-role',       verb: 'Delete',   label: 'role',   path: '/system?tab=user_management', destructive: true  };
									case 'open-user-by-name':      return { kind: 'user',   action: 'open-user',         verb: 'Open',     label: 'user',   path: '/system?tab=user_management', destructive: false };
									case 'edit-user-by-name':      return { kind: 'user',   action: 'edit-user',         verb: 'Edit',     label: 'user',   path: '/system?tab=user_management', destructive: false };
									case 'delete-user-by-name':    return { kind: 'user',   action: 'delete-user',       verb: 'Delete',   label: 'user',   path: '/system?tab=user_management', destructive: true  };
									case 'suspend-user-by-name':   return { kind: 'user',   action: 'suspend-user',      verb: 'Suspend',  label: 'user',   path: '/system?tab=user_management', destructive: true  };
									default:                       return null;
								}
							})();
							if (fastPath) {
								const name = String(result.args?.name || '').trim();
								if (name) {
									// User-management intents navigate directly (no
									// accordion to expand) — the Roles + Users tables
									// mount on the page itself. Site / client intents
									// expand an accordion on /Dashboard first.
									const path = fastPath.path || '/Dashboard';
									const postNavAction = fastPath.expand
										? [`expand-widget:${fastPath.expand}`, `${fastPath.action}:${name}`]
										: [`${fastPath.action}:${name}`];
									const offer = {
										path,
										label: `${fastPath.verb} ${fastPath.label} "${name}"`,
										postNavAction,
									};
									if (fastPath.destructive) {
										// Confirm before running anything destructive.
										const fallback = fastPath.action === 'disconnect-client'
											? `⚠️ Disconnect client "${name}" from the network?`
											: `⚠️ Delete ${fastPath.label} "${name}" permanently.`;
										const prompt = `${result.reply || fallback}\n\nConfirm by clicking Yes — or No to cancel.`;
										setPendingNavigation(offer);
										pushAssistant(prompt, offer);
									} else {
										// Non-destructive: execute immediately.
										performNavigation(offer, navigate);
										pushAssistant(
											result.reply || `${fastPath.verb === 'Open' ? 'Opening' : `${fastPath.verb}-ing`} ${fastPath.label} "${name}" now.`
										);
									}
									return;
								}
								// Missing name in args — fall through to regular path.
							}

							const ollamaHint = findHintById(result.intent);
							if (ollamaHint || findTopHfclNavigationHints(clean, { limit: 1 }).length) {
								// Use Ollama's friendly reply text if we have it;
								// the offer / disambiguation prompt comes from
								// emitNavReply.
								const llmReply = (result.reply || '').trim();
								if (llmReply) {
									pushAssistant(llmReply);
								}
								emitNavReply(ollamaHint || null);
								return;
							}
							// Unknown intent id and static found nothing — fall through.
						} else if (result.type === 'message' && result.reply) {
							// Backend may attach `suggestions` (e.g. the /help
							// fast-path returns a phrase list rendered as chips).
							const suggestions = Array.isArray(result.suggestions) && result.suggestions.length
								? { suggestions: result.suggestions }
								: null;
							pushAssistant(result.reply, suggestions);
							return;
						}
					}
				} finally {
					setLoading(false);
				}
				// Backend returned nothing usable — fall through.
			}

			// 3. Static token + Damerau-Levenshtein match — synchronous, instant.
			if (isNavigationQuestion(clean)) {
				if (emitNavReply(null)) return;
			}

			// 4. Semantic fallback — async, only when the static path missed
			// AND the Ollama backend is off (or also missed).
			if (!HFCL_USE_OLLAMA) {
				setLoading(true);
				try {
					const result = await findSemanticHint(clean, HFCL_NAVIGATION_HINTS);
					if (result?.hint) {
						const { prompt, offer } = buildNavReply(result.hint);
						setPendingNavigation(offer);
						pushAssistant(prompt, offer);
						return;
					}
				} finally {
					setLoading(false);
				}
			}

			// 5. Friendly fallback — nothing matched. Give the user obvious
			//    next steps instead of a "I don't understand" dead end.
			pushAssistant(
				"I didn't catch that. Tap one of these to get started, or type `help` for the full list.",
				{ suggestions: CLOSE_OUT_SUGGESTIONS }
			);
		},
		[
			messages,
			pendingNavigation,
			performNavigation,
			pushAssistant,
			pushUser,
			clearStatus,
			holdStatus,
		]
	);

	// Reset the whole conversation back to just the welcome bubble. Called
	// by AiChatModule when the user hits the X (close) button — that's
	// the only path that clears state. Minimize / auto-hide-on-modal /
	// action-accept all preserve the conversation for the next reopen.
	const resetChat = useCallback(() => {
		const previousSessionId = sessionIdRef.current;
		sessionIdRef.current = createSessionId();
		// Announced before anything else happens. The bridge used to learn
		// which conversation was live from whichever response arrived next,
		// so a reply already in flight when this ran would land afterwards
		// and declare its own - superseded - session current again. Telling
		// the shared lifecycle here makes everything issued before this
		// moment stale from this moment, and cancels what is still running.
		switchSession(sessionIdRef.current);
		// Told to stop, not merely ignored. The generation guards already
		// keep a late answer off the screen; this releases the request that
		// would have produced it.
		abandonInFlight();
		setMessages([buildWelcomeMessage()]);
		setPendingNavigation(null);
		clearStatus();
		setLoading(false);
		if (HFCL_USE_AGENT && previousSessionId) {
			sendAgentMessage('cancel', {
				sessionId: previousSessionId,
			}).catch(() => {
				// The new session is already active; the old Redis record will
				// expire by TTL if best-effort cancellation cannot reach the API.
			});
		}
	}, [clearStatus, abandonInFlight]);

	// Replace the live message list (used when restoring an archived thread
	// or after editing the latest user prompt and replaying from that point).
	const setChatMessages = useCallback((nextMessages) => {
		setMessages(Array.isArray(nextMessages) && nextMessages.length
			? nextMessages
			: [buildWelcomeMessage()]);
		setPendingNavigation(null);
		clearStatus();
		setLoading(false);
	}, [clearStatus]);

	return {
		messages,
		loading,
		statusLine,
		sendMessage,
		navigateFromOffer,
		resetChat,
		setChatMessages,
		// Exposed so the panel can resume an archived conversation under its
		// original session id (continuing the same server-side history)
		// instead of always talking to whatever session was already live.
		sessionIdRef,
	};
};
